import type { TournamentSummary } from "./api";

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function todayKey(now = new Date()) {
  return dateKey(now);
}

export function currentWeekRange(now = new Date()) {
  const local = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekday = local.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const monday = new Date(local);
  monday.setDate(local.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: dateKey(monday), end: dateKey(sunday) };
}

export function tournamentEnded(row: TournamentSummary, now = new Date()) {
  const today = todayKey(now);
  const end = row.endDate || row.startDate;
  return Boolean(end && end < today);
}

export function tournamentStarted(row: TournamentSummary, now = new Date()) {
  const today = todayKey(now);
  return Boolean(row.startDate && row.startDate <= today);
}

export function tournamentActive(row: TournamentSummary, now = new Date()) {
  const today = todayKey(now);
  const end = row.endDate || row.startDate;
  return Boolean(row.startDate && row.startDate <= today && (!end || end >= today));
}

export function tournamentInCurrentWeek(row: TournamentSummary, now = new Date()) {
  if (!row.startDate && !row.endDate) return false;
  const week = currentWeekRange(now);
  const start = row.startDate || row.endDate;
  const end = row.endDate || row.startDate;
  return Boolean(start && end && start <= week.end && end >= week.start);
}

export function effectiveTournamentStatus(row: TournamentSummary, now = new Date()): "planned" | "active" | "completed" {
  if (tournamentEnded(row, now)) return "completed";
  if (tournamentActive(row, now)) return "active";
  return "planned";
}

export function chooseOperationalTournament(rows: TournamentSummary[], now = new Date()): string {
  if (!rows.length) return "";
  const sorted = [...rows].sort((left, right) => {
    const leftDate = left.startDate || "9999-12-31";
    const rightDate = right.startDate || "9999-12-31";
    return leftDate.localeCompare(rightDate) || left.name.localeCompare(right.name, "pl");
  });

  const active = sorted.find((row) => tournamentActive(row, now));
  if (active) return active.id;

  const thisWeek = sorted.find((row) => tournamentInCurrentWeek(row, now) && !tournamentEnded(row, now));
  if (thisWeek) return thisWeek.id;

  const today = todayKey(now);
  const upcoming = sorted.find((row) => row.startDate && row.startDate > today);
  if (upcoming) return upcoming.id;

  const finished = sorted.filter((row) => tournamentEnded(row, now));
  return finished.at(-1)?.id || sorted[0].id;
}
